import { makeAutoObservable } from 'mobx';
import VideoCreatorStore, {
  CurrentLoadedVideo,
  DEFAULT_PREVIEW_VIDEO_RES,
} from '../VideoCreatorStore';
import { extractPolicyExpirationDate } from '@src/utility/cloudfront';
import { populateStoryQueryWithMissingFields } from '@src/utility/story';
import { deepClone } from '@src/utility/deepClone';
import { VideoVersion } from '@src/types.ts/story';

class RefreshManager {
  store: VideoCreatorStore;

  constructor(store: VideoCreatorStore) {
    makeAutoObservable(this, {
      store: false,
    });
    this.store = store;
  }

  public handleStoryPopulatedFields = async () => {
    if (!this.hasOriginalVideoExpired()) {
      return;
    }
    console.debug('RefreshManager: Cloudfront urls got stale, refreshing');

    await this.refreshStoryFields();

    // depends on story to be refreshed first
    await this.refreshVideoSource();
  };

  private hasOriginalVideoExpired(): boolean {
    const { story } = this.store;
    if (!story || !story.useAws) {
      return false;
    }

    const expirationDate = extractPolicyExpirationDate(story.originalVideo.url);
    if (!expirationDate) {
      return false;
    }

    const hasExpired = new Date() > expirationDate;
    return hasExpired;
  }

  private async refreshStoryFields() {
    const { story } = this.store;
    if (!story) {
      return;
    }
    const refreshedMissingFields =
      await populateStoryQueryWithMissingFields(story);
    if (!refreshedMissingFields) {
      return;
    }
    story.originalVideo = refreshedMissingFields.originalVideo;
    story.selfHostedVideo = refreshedMissingFields.selfHostedVideo;
  }

  private async refreshVideoSource() {
    const { story, currentVideo } = this.store;
    if (
      !story ||
      !currentVideo ||
      !story.otherVideos?.some(
        (v) =>
          v.id === currentVideo.id ||
          v.associatedVideos.find((av) => av.id === currentVideo.id),
      )
    ) {
      return;
    }
    const staleSourceToRefresh = deepClone(
      this.store.rootStore.videoSourceStore.source,
    );
    if (!staleSourceToRefresh?.elements) {
      return;
    }
    await this.cacheNewVideoSourceUrl();
    this.store.replaceWithValidVideoSourceUrl(
      staleSourceToRefresh,
      DEFAULT_PREVIEW_VIDEO_RES,
    );
    this.store.rootStore.videoSourceStore.setSource(
      staleSourceToRefresh,
      false,
    );
  }

  private async cacheNewVideoSourceUrl() {
    const { story } = this.store;
    if (!story) {
      return;
    }
    try {
      const videoSourceUrl = story.originalVideo.video.mp4UrlLow;
      if (videoSourceUrl && !this.store.cachedAssets.has(videoSourceUrl)) {
        await this.store.cacheVideoSource(videoSourceUrl);
        this.store.cachedAssets.add(videoSourceUrl);
      }
    } catch (error) {
      console.error('RefreshManager: Error caching video source:', error);
    }
  }

  public handleCurrentVideoVersion = async (
    newVideoVersionHandler?: (
      currentVideo: CurrentLoadedVideo,
      newVersion: VideoVersion,
    ) => void,
  ) => {
    const { currentVideo } = this.store;
    if (!currentVideo?.id || !this.store.isCurrentVideoTheLatest) {
      console.debug(
        'RefreshManager: No current video or not the latest version',
        [currentVideo?.id, this.store.isCurrentVideoTheLatest],
      );
      return;
    }
    if (
      this.store.renderingVideoIds.includes(currentVideo.id) ||
      this.store.isVersionsHistoryLoading
    ) {
      // Version will be updated once rendering is done, no need to check now
      console.debug(
        'RefreshManager: Current video is still rendering, skipping version check',
        currentVideo.id,
      );
      return;
    }
    try {
      const { videoVersions } =
        await this.store.datoClientStore.videoRepository.getVersionsByVideoId(
          currentVideo.id,
          5, // pageSize
          0, // page
          this.store.rootStore.userIdentityStore.isExternalUser(),
        );
      const latestVersion = videoVersions.find((v) => v.meta.is_current);
      if (
        latestVersion?.versionId &&
        latestVersion.versionId !== currentVideo.versionId
      ) {
        if (newVideoVersionHandler) {
          newVideoVersionHandler(currentVideo, latestVersion);
        } else {
          this.store.toastState = {
            state: 'warning',
            message: 'There’s a newer version of the video available…',
            position: 'top',
          };
        }
      } else {
        console.debug('RefreshManager: No new video version found', [
          currentVideo?.versionId,
          latestVersion?.versionId,
        ]);
      }
    } catch (error) {
      console.error('RefreshManager: Error fetching video versions:', error);
    }
  };
}

export default RefreshManager;
